import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { customersApi as C } from "@/lib/customers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";

const EMPTY_ADDR = { label: "", line1: "", line2: "", city: "", state: "", state_code: "", pincode: "" };
const EMPTY_CONTACT = { name: "", designation: "", phone: "", email: "", is_primary: false };

function AddressFields({ addr, onChange, states, prefix }) {
  const set = (k, v) => onChange({ ...addr, [k]: v });
  return (
    <div className="grid grid-cols-2 gap-2">
      <Input className="col-span-2" placeholder="Address line 1" value={addr.line1 || ""} onChange={(e) => set("line1", e.target.value)} data-testid={`${prefix}-line1`} />
      <Input className="col-span-2" placeholder="Address line 2" value={addr.line2 || ""} onChange={(e) => set("line2", e.target.value)} />
      <Input placeholder="City" value={addr.city || ""} onChange={(e) => set("city", e.target.value)} data-testid={`${prefix}-city`} />
      <Select value={addr.state_code || ""} onValueChange={(v) => { const s = states.find((x) => x.code === v); set("state_code", v); onChange({ ...addr, state_code: v, state: s?.name || "" }); }}>
        <SelectTrigger data-testid={`${prefix}-state`}><SelectValue placeholder="State" /></SelectTrigger>
        <SelectContent>{states.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}</SelectContent>
      </Select>
      <Input placeholder="Pincode" value={addr.pincode || ""} onChange={(e) => set("pincode", e.target.value)} />
    </div>
  );
}

export default function CustomerForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ categories: [], payment_terms: [], tax_types: [], statuses: [], states: [] });
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    company_name: "", gstin: "", pan: "", tax_type: "Registered", category: "Domestic", status: "Active",
    billing_address: { ...EMPTY_ADDR }, shipping_addresses: [], contacts: [{ ...EMPTY_CONTACT, is_primary: true }],
    payment_terms: "Advance", credit_limit: "", credit_days: "", notes: "", attachments: [],
  });

  useEffect(() => {
    C.meta().then(setMeta);
    if (editing) C.get(id).then((d) => { setF({ ...d, credit_limit: d.credit_limit || "", credit_days: d.credit_days || "" }); }).finally(() => setLoading(false));
  }, [id, editing]);

  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const updShip = (i, v) => setF((p) => ({ ...p, shipping_addresses: p.shipping_addresses.map((a, idx) => idx === i ? v : a) }));
  const updContact = (i, k, v) => setF((p) => ({ ...p, contacts: p.contacts.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));

  const save = async () => {
    if (!f.company_name) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...f, credit_limit: Number(f.credit_limit) || 0, credit_days: Number(f.credit_days) || 0,
        contacts: f.contacts.filter((c) => c.name), shipping_addresses: f.shipping_addresses.filter((a) => a.line1 || a.city) };
      const res = editing ? await C.update(id, payload) : await C.create(payload);
      if (!editing && res.linked_enquiries) toast.success(`Customer created · linked ${res.linked_enquiries} past enquiry(ies)`);
      else toast.success(editing ? "Customer updated" : "Customer created");
      navigate(`/customers/${res.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  return (
    <div className="space-y-6" data-testid="customer-form-page">
      <button onClick={() => navigate("/customers")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" />Back</button>
      <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{editing ? "Edit Customer" : "New Customer"}</h2>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <h3 className="mb-4 font-heading font-semibold text-slate-900">Company & Tax</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5"><Label>Company name *</Label><Input value={f.company_name} onChange={(e) => upd("company_name", e.target.value)} data-testid="cust-company-input" /></div>
            <div className="space-y-1.5"><Label>GSTIN</Label><Input value={f.gstin} onChange={(e) => upd("gstin", e.target.value)} data-testid="cust-gstin-input" /></div>
            <div className="space-y-1.5"><Label>PAN</Label><Input value={f.pan} onChange={(e) => upd("pan", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Tax type</Label>
              <Select value={f.tax_type} onValueChange={(v) => upd("tax_type", v)}><SelectTrigger data-testid="cust-taxtype-select"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.tax_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Category</Label>
              <Select value={f.category} onValueChange={(v) => upd("category", v)}><SelectTrigger data-testid="cust-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => upd("status", v)}><SelectTrigger data-testid="cust-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="mb-4 font-heading font-semibold text-slate-900">Payment & Credit</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Payment terms</Label>
              <Select value={f.payment_terms} onValueChange={(v) => upd("payment_terms", v)}><SelectTrigger data-testid="cust-terms-select"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.payment_terms.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Credit days</Label><Input type="number" value={f.credit_days} onChange={(e) => upd("credit_days", e.target.value)} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Credit limit (₹)</Label><Input type="number" value={f.credit_limit} onChange={(e) => upd("credit_limit", e.target.value)} data-testid="cust-credit-input" /></div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="mb-4 font-heading font-semibold text-slate-900">Billing address</h3>
          <AddressFields addr={f.billing_address} onChange={(v) => upd("billing_address", v)} states={meta.states} prefix="billing" />
        </Card>

        <Card className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-900">Shipping addresses</h3>
            <Button size="sm" variant="outline" onClick={() => upd("shipping_addresses", [...f.shipping_addresses, { ...EMPTY_ADDR }])} data-testid="add-shipping-btn"><Plus className="mr-1 h-3.5 w-3.5" />Add</Button>
          </div>
          {f.shipping_addresses.length === 0 && <p className="text-sm text-slate-400">No separate shipping address (billing used by default).</p>}
          <div className="space-y-4">
            {f.shipping_addresses.map((a, i) => (
              <div key={i} className="rounded-md border border-slate-200 p-3" data-testid={`shipping-${i}`}>
                <Input className="mb-2" placeholder="Label (e.g. Warehouse)" value={a.label || ""} onChange={(e) => updShip(i, { ...a, label: e.target.value })} />
                <AddressFields addr={a} onChange={(v) => updShip(i, v)} states={meta.states} prefix={`ship-${i}`} />
                <button className="mt-2 flex items-center gap-1 text-xs text-rose-600" onClick={() => upd("shipping_addresses", f.shipping_addresses.filter((_, idx) => idx !== i))}><Trash2 className="h-3.5 w-3.5" />Remove</button>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-900">Contact persons</h3>
            <Button size="sm" variant="outline" onClick={() => upd("contacts", [...f.contacts, { ...EMPTY_CONTACT }])} data-testid="add-contact-btn"><Plus className="mr-1 h-3.5 w-3.5" />Add</Button>
          </div>
          <div className="space-y-3">
            {f.contacts.map((c, i) => (
              <div key={i} className="grid grid-cols-2 gap-2 rounded-md border border-slate-200 p-3 lg:grid-cols-4" data-testid={`contact-${i}`}>
                <Input placeholder="Name" value={c.name} onChange={(e) => updContact(i, "name", e.target.value)} data-testid={`contact-name-${i}`} />
                <Input placeholder="Designation" value={c.designation} onChange={(e) => updContact(i, "designation", e.target.value)} />
                <Input placeholder="Phone" value={c.phone} onChange={(e) => updContact(i, "phone", e.target.value)} data-testid={`contact-phone-${i}`} />
                <Input placeholder="Email" value={c.email} onChange={(e) => updContact(i, "email", e.target.value)} />
                <label className="col-span-2 flex items-center gap-2 text-xs text-slate-600 lg:col-span-4">
                  <input type="checkbox" checked={!!c.is_primary} onChange={(e) => updContact(i, "is_primary", e.target.checked)} /> Primary contact
                  {f.contacts.length > 1 && <button className="ml-auto flex items-center gap-1 text-rose-600" onClick={() => upd("contacts", f.contacts.filter((_, idx) => idx !== i))}><Trash2 className="h-3.5 w-3.5" />Remove</button>}
                </label>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 lg:col-span-2">
          <h3 className="mb-4 font-heading font-semibold text-slate-900">Notes</h3>
          <Textarea value={f.notes} onChange={(e) => upd("notes", e.target.value)} rows={3} placeholder="Internal notes about this customer" />
        </Card>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate("/customers")}>Cancel</Button>
        <Button onClick={save} disabled={saving} data-testid="save-customer-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editing ? "Save changes" : "Create customer"}
        </Button>
      </div>
    </div>
  );
}
